/**
 * EmailNoDraftBanner — shown inside the opened email when the pipeline
 * produced NO draft order for an ORDER-classified email. Three genuine
 * no-draft cases, framed differently:
 *
 *  - thread_dup (positive / green): an order WAS already created earlier in
 *      this same thread — a forward/reply re-send. We surface that existing
 *      order at the top as a positive, one-click "Open order" link rather
 *      than telling the CSR an order "was not generated" (Scott's reframe).
 *  - order_revision / agent_marked_revision (warning / amber): this email
 *      looks like a requested CHANGE to an order already created. Nothing is
 *      applied automatically — ever (Scott's decision, review cc1d526a); the
 *      banner links the existing order. When Merlin persisted a confirmable
 *      edit proposal there (merlin_proposal_id), the CTA reads "review
 *      proposed changes"; otherwise the CSR applies edits manually.
 *  - hard_failure (error / red): an unrecoverable processing error; no order
 *      exists anywhere. Keeps the "an order was not generated for this email"
 *      framing; flagged for the dev team, the CSR enters the order manually.
 *  - late_sync_already_ordered (positive / green): the email reached the
 *      system days after it was sent (mail sync gap, kanban ebc7bfed) and by
 *      then the desk had already entered its PO in Sage. Nothing to enter;
 *      the banner names the Sage order (openable only when it is also a
 *      portal order) and says how late the email was.
 *
 * The common path (a draft WAS created — including dup-PO cross-thread and
 * no-customer) does NOT use this banner; those show the dup-PO banner or the
 * partial draft instead.
 */
import React, { useState } from 'react';
import type { PortalNoDraftReason } from '../../types/portal';
import styles from './EmailNoDraftBanner.module.css';

/** Combined text length above which the revision banner collapses its
 *  detail text behind "Show more" (review b6db3995: the amber box was
 *  eating a bunch of vertical space). */
const REVISION_TEXT_CLAMP_THRESHOLD = 160;

interface EmailNoDraftBannerProps {
  reason: PortalNoDraftReason | null | undefined;
  /** Opens the referenced existing order (thread_dup case). Omit to hide the button. */
  onOpenOrder?: (orderId: string) => void;
}

const THREAD_DUP_DETAIL =
  'This message is a re-send of a PO that was already ordered, so no new draft was created.';
const ORDER_REVISION_DETAIL =
  'This email looks like a change to an existing PO. No changes were made automatically — ' +
  'review the existing order and apply any edits manually.';
const PROPOSAL_READY_DETAIL =
  'Merlin prepared the proposed changes from this email. Nothing has been applied — ' +
  'open the order to review and confirm (or dismiss) them.';
const NO_MATCHING_ORDER_DETAIL =
  'The agent read this email as a revision of an already-placed order, but no ' +
  'matching order was found for this customer + PO. If this is actually a new ' +
  'order, enter it manually — nothing was created automatically.';
const HARD_FAILURE_DETAIL =
  "We couldn't process this order automatically, so no draft was saved. It's been " +
  'flagged for the dev team — please enter this order manually for now.';

const LATE_SYNC_ALREADY_ORDERED_TITLE =
  'Late-synced email — this PO was already entered in Sage';

/** Fallback copy when the backend detail is missing (historical rows). */
function lateSyncAlreadyOrderedDetail(reason: PortalNoDraftReason): string {
  const days = reason.late_sync_gap_days;
  const lateness =
    typeof days === 'number' ? `${days} day${days === 1 ? '' : 's'} late` : 'late';
  const orderNo = reason.existing_order?.order_no ?? reason.existing_order?.sage_order_no;
  const status = reason.existing_order?.status;
  const entered = orderNo
    ? ` Its PO was already entered as ${orderNo}${status ? ` (${status})` : ''}.`
    : ' Its PO was already entered in Sage.';
  return `This email reached the system ${lateness} because of a mail sync gap.${entered} No order was created.`;
}

const EXTRACTION_DELEGATED_DETAIL =
  'This email was classified as an order, but the order extractor did not run for it, ' +
  'so no draft was saved. Please enter this order manually from the source PO.';

const EmailNoDraftBanner: React.FC<EmailNoDraftBannerProps> = ({ reason, onOpenOrder }) => {
  // Long revision text starts collapsed (2-line clamp per paragraph) so the
  // banner never dominates the email view; "Show more" expands in place.
  const [textExpanded, setTextExpanded] = useState(false);

  if (!reason) return null;

  // thread_dup: an order WAS already created earlier in this thread. Surface
  // it positively as a one-click link to that order — do NOT tell the CSR an
  // order "was not generated" (Scott's reframe).
  if (reason.type === 'thread_dup') {
    const existing = reason.existing_order;
    const openableOrderId = existing?.order_id ?? null;
    const detail = reason.detail || THREAD_DUP_DETAIL;
    const orderNo = existing?.order_no;
    const poNumber = reason.po_number || existing?.po_number;
    const status = existing?.status;
    // Cross-thread dedup (reply to our own confirmation / exact re-statement
    // from another email) must not claim the order came from "this thread".
    const title =
      reason.subtype === 'cross_thread_po_dup'
        ? 'Order already created from another email'
        : 'Order already created earlier in this thread';

    return (
      <div
        className={`${styles.banner} ${styles.created}`}
        role="status"
        data-testid="email-no-draft-banner"
        data-reason="thread_dup"
      >
        <span className={styles.icon} aria-hidden="true">
          ✓
        </span>
        <div className={styles.body}>
          <div className={styles.title}>{title}</div>
          {(poNumber || orderNo || status) && (
            <div className={styles.meta} data-testid="thread-dup-meta">
              {poNumber && <span>PO {poNumber}</span>}
              {orderNo && (
                <span>
                  &rarr; order <strong>{orderNo}</strong>
                </span>
              )}
              {status && <span>&middot; {status}</span>}
            </div>
          )}
          <div className={styles.text}>{detail}</div>
          {openableOrderId && onOpenOrder && (
            <div className={styles.actions}>
              <button
                type="button"
                className={styles.action}
                onClick={() => onOpenOrder(openableOrderId)}
                data-action="order_open"
                data-target-order-id={openableOrderId}
                data-outcome="open"
              >
                {orderNo ? `Open order ${orderNo}` : 'Open existing order'}
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // late_sync_already_ordered: a catch-up sync delivered an old email whose
  // PO the desk already keyed into Sage. Positive framing like thread_dup —
  // the work is done — plus the two clocks so "why is an August email here
  // today" answers itself. Open button only for a portal order (a PO keyed
  // straight into Sage has no portal order to open).
  if (reason.type === 'late_sync_already_ordered') {
    const existing = reason.existing_order;
    const openableOrderId = existing?.order_id ?? null;
    const orderNo = existing?.order_no ?? existing?.sage_order_no;
    const poNumber = reason.po_number || existing?.po_number;
    const status = existing?.status;
    const detail = reason.detail || lateSyncAlreadyOrderedDetail(reason);
    const received = reason.late_sync_received_at?.slice(0, 10);
    const synced = reason.late_sync_synced_at?.slice(0, 10);

    return (
      <div
        className={`${styles.banner} ${styles.created}`}
        role="status"
        data-testid="email-no-draft-banner"
        data-reason="late_sync_already_ordered"
      >
        <span className={styles.icon} aria-hidden="true">
          ✓
        </span>
        <div className={styles.body}>
          <div className={styles.title}>{LATE_SYNC_ALREADY_ORDERED_TITLE}</div>
          {(poNumber || orderNo || status || received) && (
            <div className={styles.meta} data-testid="late-sync-already-ordered-meta">
              {poNumber && <span>PO {poNumber}</span>}
              {orderNo && (
                <span>
                  &rarr; order <strong>{orderNo}</strong>
                </span>
              )}
              {status && <span>&middot; {status}</span>}
              {received && synced && (
                <span>
                  &middot; sent {received}, synced {synced}
                </span>
              )}
            </div>
          )}
          <div className={styles.text}>{detail}</div>
          {openableOrderId && onOpenOrder && (
            <div className={styles.actions}>
              <button
                type="button"
                className={styles.action}
                onClick={() => onOpenOrder(openableOrderId)}
                data-action="order_open"
                data-target-order-id={openableOrderId}
                data-outcome="open"
              >
                {orderNo ? `Open order ${orderNo}` : 'Open existing order'}
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // order_revision / agent_marked_revision: this email looks like a requested
  // change to an already-created order. Warn the CSR and link the existing
  // order. When Merlin persisted a confirmable edit proposal on that order
  // (merlin_proposal_id), the CTA reads "review proposed changes" — the CSR
  // confirms or dismisses the old→new card on the order itself. Nothing is
  // ever applied without CSR confirmation (Scott's decision, review cc1d526a).
  if (reason.type === 'order_revision' || reason.type === 'agent_marked_revision') {
    const existing = reason.existing_order;
    const hasProposal = Boolean(reason.merlin_proposal_id);
    // No matching order anywhere (portal or Sage mirror): the agent's
    // revision call is unverified and often wrong (a boilerplate "changed
    // since previous revision" form legend on a brand-new PO). The banner
    // must NOT assert an existing order it can't point at. Historical rows
    // carry the old misleading detail text, so the honest copy overrides
    // the stored detail here.
    const noMatch = reason.type === 'agent_marked_revision' && !existing;
    const detail = noMatch
      ? NO_MATCHING_ORDER_DETAIL
      : reason.detail || (hasProposal ? PROPOSAL_READY_DETAIL : ORDER_REVISION_DETAIL);
    // agent_marked_revision reasons carry the backend lookup shape
    // (sage_order_no); the thread-FSM shape uses order_no.
    const orderNo = existing?.order_no ?? existing?.sage_order_no;
    const poNumber = reason.po_number || existing?.po_number;
    const status = existing?.status;
    // Sage-mirror matches have no portal order to open (order_id null).
    const openableOrderId = existing?.order_id ?? null;

    return (
      <div
        className={`${styles.banner} ${styles.warning}`}
        role="alert"
        data-testid="email-no-draft-banner"
        data-reason={reason.type}
      >
        <span className={styles.icon} aria-hidden="true">
          ⚠
        </span>
        <div className={styles.body}>
          {/* Line 1: title + PO→order meta inline (3-line budget when
              collapsed — review 1058222a: "three lines max"). */}
          <div className={styles.titleRow}>
            <span className={styles.title}>
              {reason.type === 'agent_marked_revision'
                ? noMatch
                  ? 'Flagged as a revision, but no matching order was found'
                  : 'This email requests changes to an existing order'
                : 'This email may request changes to an existing order'}
            </span>
            {(poNumber || orderNo || status) && (
              <span className={styles.metaInline} data-testid="order-revision-meta">
                {poNumber && <span>PO {poNumber}</span>}
                {orderNo && (
                  <span>
                    &rarr; <strong>{orderNo}</strong>
                  </span>
                )}
                {status && <span>&middot; {status}</span>}
              </span>
            )}
          </div>
          {/* Line 2: ONE line of text (the agent's delta when present) with
              an inline Show more; expanding reveals everything in place. */}
          {(() => {
            const primaryText = reason.revision_details || detail;
            const clampable =
              (Boolean(reason.revision_details) && Boolean(detail)) ||
              primaryText.length > REVISION_TEXT_CLAMP_THRESHOLD;
            if (!clampable || textExpanded) {
              return (
                <>
                  {reason.revision_details && (
                    <div className={styles.text} data-testid="revision-details">
                      {reason.revision_details}
                    </div>
                  )}
                  <div className={styles.text}>{detail}</div>
                  {clampable && (
                    <button
                      type="button"
                      className={styles.textToggle}
                      data-testid="revision-text-toggle"
                      onClick={() => setTextExpanded(false)}
                    >
                      Show less
                    </button>
                  )}
                </>
              );
            }
            return (
              <div className={styles.textLineRow}>
                <div
                  className={`${styles.text} ${styles.textClampedOne}`}
                  data-testid="revision-details"
                >
                  {primaryText}
                </div>
                <button
                  type="button"
                  className={styles.textToggle}
                  data-testid="revision-text-toggle"
                  onClick={() => setTextExpanded(true)}
                >
                  Show more
                </button>
              </div>
            );
          })()}
          {/* Line 3: the one-click action (portal orders only — a
              Sage-mirror match has no portal order to open). */}
          {openableOrderId && onOpenOrder && (
            <div className={styles.actionsTight}>
              <button
                type="button"
                className={styles.action}
                onClick={() => onOpenOrder(openableOrderId)}
                data-action="order_open"
                data-target-order-id={openableOrderId}
                data-outcome="open"
              >
                {hasProposal
                  ? orderNo
                    ? `Review proposed changes on ${orderNo}`
                    : 'Review proposed changes'
                  : orderNo
                    ? `Review order ${orderNo}`
                    : 'Review existing order'}
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // extraction_delegated: the classifier saw an order but the extractor
  // (Merlin) did not run for it, so there were no lines to draft from. Not a
  // failure — an amber "enter it manually" note that names the PO.
  if (reason.type === 'extraction_delegated') {
    const delegatedDetail = reason.detail || EXTRACTION_DELEGATED_DETAIL;
    return (
      <div
        className={`${styles.banner} ${styles.warning}`}
        role="status"
        data-testid="email-no-draft-banner"
        data-reason="extraction_delegated"
      >
        <span className={styles.icon} aria-hidden="true">
          ⚠
        </span>
        <div className={styles.body}>
          <div className={styles.title}>
            No draft was created for this order
            {reason.po_number ? ` (PO ${reason.po_number})` : ''}.
          </div>
          <div className={styles.text}>{delegatedDetail}</div>
        </div>
      </div>
    );
  }

  // hard_failure: a genuine processing error — no order exists anywhere. Keep
  // the "an order was not generated → enter manually" red alert.
  const detail = reason.detail || HARD_FAILURE_DETAIL;
  return (
    <div
      className={`${styles.banner} ${styles.error}`}
      role="alert"
      data-testid="email-no-draft-banner"
      data-reason="hard_failure"
    >
      <span className={styles.icon} aria-hidden="true">
        ⚠
      </span>
      <div className={styles.body}>
        <div className={styles.title}>An order was not generated for this email.</div>
        <div className={styles.text}>{detail}</div>
      </div>
    </div>
  );
};

export default EmailNoDraftBanner;
